import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AuthSession, Me } from '@blackblack/shared';
import { api, onSessionChange, refreshSession, setAccessToken } from '../lib/api';
import { clearAllQueries, resetInfinite } from '../lib/query';
import { clearEntityPatches } from '../lib/entities';

type Status = 'loading' | 'authed' | 'anon';

interface AuthContextValue {
  status: Status;
  me: Me | null;
  login(identifier: string, password: string): Promise<void>;
  register(input: { username: string; email: string; password: string; displayName?: string }): Promise<void>;
  logout(): Promise<void>;
  setMe(me: Me): void;
  refreshMe(): Promise<void>;
  /** Returns true when logged in; otherwise opens the login prompt. */
  requireAuth(reason?: string): boolean;
  authPrompt: string | null;
  closeAuthPrompt(): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [me, setMeState] = useState<Me | null>(null);
  const [authPrompt, setAuthPrompt] = useState<string | null>(null);

  const apply = useCallback((s: AuthSession | null) => {
    setAccessToken(s?.accessToken ?? null);
    setMeState(s?.user ?? null);
    setStatus(s ? 'authed' : 'anon');
  }, []);

  useEffect(() => {
    onSessionChange((s) => {
      if (!s) apply(null);
    });
    void refreshSession().then(apply);
  }, [apply]);

  // Refresh the access token shortly before it expires.
  useEffect(() => {
    if (status !== 'authed') return;
    const t = setInterval(() => void refreshSession().then((s) => s && setMeState(s.user)), 12 * 60 * 1000);
    return () => clearInterval(t);
  }, [status]);

  // Keep the unread badge fresh.
  useEffect(() => {
    if (status !== 'authed') return;
    const poll = () =>
      void api
        .get<{ count: number }>('/notifications/unread-count')
        .then(({ count }) => setMeState((m) => (m && m.unreadNotifications !== count ? { ...m, unreadNotifications: count } : m)))
        .catch(() => {});
    const t = setInterval(poll, 30_000);
    window.addEventListener('focus', poll);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', poll);
    };
  }, [status]);

  const afterIdentityChange = () => {
    clearAllQueries();
    clearEntityPatches();
    resetInfinite('');
  };

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      me,
      async login(identifier, password) {
        const s = await api.post<AuthSession>('/auth/login', { identifier, password });
        afterIdentityChange();
        apply(s);
        setAuthPrompt(null);
      },
      async register(input) {
        const s = await api.post<AuthSession>('/auth/register', input);
        afterIdentityChange();
        apply(s);
        setAuthPrompt(null);
      },
      async logout() {
        await api.post('/auth/logout').catch(() => {});
        afterIdentityChange();
        apply(null);
      },
      setMe: setMeState,
      async refreshMe() {
        setMeState(await api.get<Me>('/users/me'));
      },
      requireAuth(reason) {
        if (status === 'authed') return true;
        setAuthPrompt(reason ?? 'Log in om verder te gaan');
        return false;
      },
      authPrompt,
      closeAuthPrompt: () => setAuthPrompt(null),
    }),
    [status, me, apply, authPrompt],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
